import {Component, Input } from '@angular/core';
import * as Highcharts from 'highcharts/highstock';
import HC_exporting from 'highcharts/modules/exporting';
import HC_exporting_data from 'highcharts/modules/export-data';
import HC_xrange from 'highcharts/modules/xrange';
import { I18NService } from '../../../shared/i18n/i18n.service';
import { TankBook } from '../tank-books/tank-book';
import { TankLog } from '../tank-log';

HC_exporting(Highcharts);
HC_exporting_data(Highcharts);
HC_xrange(Highcharts);

declare module 'highcharts' {
  interface Point {
    highlight: (event: Highcharts.PointerEventObject) => void;
  }
}

Highcharts.Pointer.prototype.reset = function() {
  return undefined;
};

Highcharts.Point.prototype.highlight = function(event) {
  event = this.series.chart.pointer.normalize(event);
  this.onMouseOver(event);
  this.series.chart.tooltip.refresh(this);
  this.series.chart.xAxis[0].drawCrosshair(event, this);
};

@Component({
  selector: 'app-levels-chart',
  templateUrl: './levels-chart.component.html',
  styleUrls: ['./levels-chart.component.css']
})
export class LevelsChartComponent{

  Highcharts: typeof Highcharts = Highcharts;
  chartConstructor: string = 'chart';
  chartOptionsLevels: Highcharts.Options = {  };
  chartOptionsDifference: Highcharts.Options = {  };
  chartOptionsSales: Highcharts.Options = {  };
  chart: Highcharts.Chart;
  chartCallback: Highcharts.ChartCallbackFunction;

  synchronizeTooltips = (e: any) => {
    var chart,
      point,
      i,
      event,
      charts = Highcharts.charts,
      sourceChart = charts[(Highcharts as any).hoverChartIndex];

    for (i = 0; i < charts.length; i = i + 1) {
      chart = charts[i];

      if (
        chart &&
        (
          chart.options.chart &&
          chart.options.chart.className
        ) === (
          sourceChart.options.chart &&
          sourceChart.options.chart.className
        )
      ) {
        event = chart.pointer.normalize(e);

        point = (chart.series[0] as any).searchPoint(event, true);

        if (point) {
          point.highlight(e);
        }
      }
    }
  };

  clearTooltips = (e: any) => {
    var chart,
      point,
      i,
      event,
      charts = Highcharts.charts;

    for (i = 0; i < charts.length; i = i + 1) {
      chart = charts[i];
      if( chart ) {
        event = chart.pointer.normalize(e);
        point = (chart.series[0] as any).searchPoint(event, true);
        if( point ) {
          point.onMouseOut();
          chart.tooltip.hide(point);
          chart.xAxis[0].hideCrosshair();
        }
      }
    }
  }

  constructor(private i18nService: I18NService) {
    var self = this;
    this.chartCallback = chart => { self.chart = chart; };
  }

  @Input()
  currentTank: TankLog;

  @Input()
  set tankBookSeries(tankBook: TankBook[]) {
    this.updateValues(tankBook)
  }

  private updateValues(tankBook: TankBook[]) {
    this.chartConstructor = "stockChart";
    this.chartOptionsLevels = this.getLevelsChartConfig(tankBook);
    this.chartOptionsDifference = this.getDifferenceChartConfig(tankBook);
    this.chartOptionsSales = this.getSalesChartConfig(tankBook);
  }

  private getLevelsChartConfig(tankBook: TankBook[]): Highcharts.Options {
    let chartOptions = this.getBaseChartConfig();
    chartOptions.chart.type = "line";
    chartOptions.colors = ["#04afa0","#0d3f85"];
    chartOptions.series = this.parseLevelSeries(tankBook);
    chartOptions.scrollbar = {enabled : false};
    chartOptions.navigator = {enabled : false};
    chartOptions.yAxis = {
      title: {
        text: this.i18nService.get('volume') + ' (L)'
      },
      opposite: false,
      resize: {
        enabled: true
      },
      labels: {
        formatter: function () {
          return this.value + '';
        }
      }
    };

    return chartOptions;
  }

  private getDifferenceChartConfig(tankBook: TankBook[]): Highcharts.Options {
    let chartOptions = this.getBaseChartConfig();
    chartOptions.chart.type = "line";
    chartOptions.colors = ["#AF2A0A"],
    chartOptions.series = this.parseDifferenceSeries(tankBook);
    chartOptions.scrollbar = {enabled : false};
    chartOptions.navigator = {enabled: false};
    chartOptions.yAxis = {
      title: {
        text: this.i18nService.get('difference')
      },
      opposite: false,
      resize: {
        enabled: true
      },
      labels: {
        formatter: function () {
          return this.value + '';
        }
      }
    };
    return chartOptions;
  }

  private getSalesChartConfig(tankBook: TankBook[]): Highcharts.Options {
    let chartOptions = this.getBaseChartConfig();
    let totalSales = 0;
    chartOptions.chart.type = "line";
    chartOptions.colors = ["#025c37"];
    chartOptions.series = this.parseSalesSeries(tankBook);
    // @ts-ignore
    chartOptions.series[0].data.forEach( data => {
      totalSales += data[1];
    });

    chartOptions.title = {
      text: this.i18nService.get('sales') + ': ' + Highcharts.numberFormat(totalSales, 2, '.', ',') + ' L',
      align: 'left'
    }
    chartOptions.yAxis = {
      title: {
        text: this.i18nService.get('salesPos')
      },
      opposite: false,
      resize: {
        enabled: true
      },
      labels: {
        formatter: function () {
          return this.value + '';
        }
      }
    };

    return chartOptions;
  }

  private getBaseChartConfig(): Highcharts.Options {
    return {
      chart: {
        zoomType: 'x',
        title: '',
        className: 'syncronized-chart'
      },
      tooltip: {
        valueDecimals: 2,
        crosshairs: true,
        split: false,
        xDateFormat: '%H:%M:%S %e/%m'
      },
      rangeSelector: {
        inputEnabled: false,
        enabled: false
      },
      xAxis: {
        type: 'datetime',
        crosshair: true,
        events: {
          setExtremes: function(e: any) {
            var thisChart = (this as Highcharts.Axis).chart;

            if (e.trigger !== "syncExtremes") {
              Highcharts.charts.forEach(function(chart) {
                if ( chart  && chart !== thisChart &&
                   ( chart.options.chart && chart.options.chart.className) ===
                   ( thisChart.options.chart && thisChart.options.chart.className)
                   ) {
                  if (chart.xAxis[0].setExtremes) {
                    chart.xAxis[0].setExtremes(e.min, e.max, undefined, false, {
                      trigger: "syncExtremes"
                    });
                  }
                }
              });
            }
          }
        }
      },
      exporting: {
        enabled: false
      },
      credits: {
        enabled: false
      },
      legend: {
        enabled: false
      },
    } as Highcharts.Options;
  }

  private parseLevelSeries(tankBook: TankBook[]): any[]{
    let series = [];
    let levelSerie =  {
      data: [],
      name: this.i18nService.get('volume') + ' (L)'
    };

    if(tankBook.length > 0) {
      tankBook.forEach(tank => {
        levelSerie.data.push([tank.day, tank.closingLevel]);
      });
    }

    series.push(levelSerie);
    return series;
  }


  private parseDifferenceSeries(tankBook: TankBook[]): any[]{
    let series = [];
    let differenceSerie =  {
      data: [],
      name: this.i18nService.get('difference')
    };

    if(tankBook.length > 0) {
      tankBook.forEach(tank => {
        differenceSerie.data.push([tank.day, tank.difference]);
      });
    }

    series.push(differenceSerie);
    return series;
  }


  private parseSalesSeries(tankBook: TankBook[]): any[]{
    let series = [];
    let salesPosSerie =  {
      data: [],
      name: this.i18nService.get('salesPos')
    };

    if(tankBook.length > 0) {
      tankBook.forEach(tank => {
        salesPosSerie.data.push([tank.day, tank.salesPos]);
      });
    }

    series.push(salesPosSerie);
    return series;
  }

}

